import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { msg } from '@tov-plus/ui';

export type ConnectivityState = {
  isOnline: boolean;
  isOffline: boolean;
  lastChangedAt: Date | null;
};

type ConnectivitySubscriber = (state: ConnectivityState) => void;

function getBrowserOnlineState() {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

function createConnectivityState(
  isOnline: boolean,
  lastChangedAt: Date | null,
): ConnectivityState {
  return {
    isOnline,
    isOffline: !isOnline,
    lastChangedAt,
  };
}

export function subscribeToConnectivityChanges(
  subscriber: ConnectivitySubscriber,
) {
  if (typeof window === 'undefined') {
    subscriber(createConnectivityState(true, null));
    return () => undefined;
  }

  const emit = (lastChangedAt: Date | null) => {
    subscriber(createConnectivityState(getBrowserOnlineState(), lastChangedAt));
  };
  const handleConnectivityChange = () => emit(new Date());

  emit(null);
  window.addEventListener('online', handleConnectivityChange);
  window.addEventListener('offline', handleConnectivityChange);

  return () => {
    window.removeEventListener('online', handleConnectivityChange);
    window.removeEventListener('offline', handleConnectivityChange);
  };
}

const ConnectivityContext = createContext<ConnectivityState | null>(null);

export function ConnectivityProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState(() =>
    createConnectivityState(getBrowserOnlineState(), null),
  );

  useEffect(() => subscribeToConnectivityChanges(setState), []);

  const value = useMemo(
    () => ({
      isOnline: state.isOnline,
      isOffline: state.isOffline,
      lastChangedAt: state.lastChangedAt,
    }),
    [state.isOffline, state.isOnline, state.lastChangedAt],
  );

  return (
    <ConnectivityContext.Provider value={value}>
      {children}
    </ConnectivityContext.Provider>
  );
}

export function useConnectivity() {
  const context = useContext(ConnectivityContext);

  if (!context) {
    return createConnectivityState(getBrowserOnlineState(), null);
  }

  return context;
}

export function useOnlineActionGuard(onBlocked?: (message: string) => void) {
  const connectivity = useConnectivity();

  return useCallback(
    (
      message = msg(
        'connectivity.use_online_action_guard.this_action_requires_an_internet_connection',
      ),
    ) => {
      if (connectivity.isOnline) {
        return true;
      }

      onBlocked?.(message);
      return false;
    },
    [connectivity.isOnline, onBlocked],
  );
}
